import { useContext, useEffect, useMemo, useState } from "react";
import _ from "underscore";
import services from "../../../services/services";
import { Urls } from "../../../services/Urls";
import { ToastContext } from "../../../context/ToastContext";
import { useChartConfig } from "../Overview/useChartConfig";

export const useUniHelpdeskCharts = ({ chart, setChart, columns, rowData }) => {

    const { showSuccess, showInfo, showError } = useContext(ToastContext);
    const [createChart, setCreateChart] = useState({ width: "50", height: 300, chart_scope_key: "universal_helpdesk" });
    const [options, setOptions] = useState({ xAxis: [] });
    const { handleSave, getChartConfiguration, allCharts, createOpts, handleDelete, loader } = useChartConfig({ createChart: null, rowData: null, columnDefs: null });
    const [savedCharts, setSavedCharts] = useState([]);
    const [deleteChart, setDeleteChart] = useState({ visible: false, data: [] });

    useEffect(() => {
        getChartConfiguration("universal_helpdesk");
    }, [rowData])

    useEffect(() => {
        setOptions((prev) => ({ ...prev, xAxis: _.sortBy(columns, "headerName")?.map(item => { return { columnName: item?.headerName, key: item?.field } })?.filter(item => item?.key !== "id" && item?.key !== "attachments" & item?.key !== "log") }));
    }, [columns, rowData])

    useEffect(() => {
        getFormattedData(createChart);
    }, [createChart?.xAxis, createChart?.yAxis, rowData])

    useEffect(() => {
        if (!_.isEmpty(allCharts)) {
            let charts = [];
            allCharts?.forEach(item => {
                const type = _.findWhere(createOpts?.chartOpts, { chart_type_id: item?.chart_type_id });
                let obj = {
                    ...item,
                    xAxis: item?.chart_x_axis,
                    chartName: item?.chart_name,
                    chartType: type || "",
                }
                const formatted = getFormattedData(obj, true)
                obj["options"] = generateOptions({ ...obj, formattedData: formatted });
                charts?.push(obj);
            })
            setSavedCharts(charts);
        }
    }, [allCharts])

    const saveData = async () => {
        try {
            if (_.isEmpty(createChart?.chartType)) {
                showInfo("info", "Please select chart type");
                return;
            } else if (!createChart?.xAxis) {
                showInfo("info", "Please select x axis");
                return;
            } else {
                await handleSave(createChart);
                setChart(prev => ({ ...prev, visible: false }));
                setCreateChart({ width: "50", height: 300, chart_scope_key: "universal_helpdesk" });
            }
        } catch (error) {
            console.error("error saving chart config", error);
            showError("error", error?.response?.data?.errorMessage);
        }
    }

    const getFormattedData = (chartData, returnData) => {
        let data = [];
        const dataValues = _.uniq(_.pluck(rowData, chartData?.xAxis)?.filter(item => item));
        dataValues?.forEach(item => {
            data.push({ [chartData?.xAxis]: item })
        })
        data?.forEach(item => {
            const dataCount = _.where(rowData, item);
            item["requests"] = dataCount?.length || 0;
        })
        if (returnData) {
            return data;
        } else {
            setCreateChart((prev) => ({ ...prev, formattedData: data }));
        }
    }

    const generateOptions = (value) => {
        let seriesData = [];
        if (value?.chartType?.chart_type_key === 'pie') {
            seriesData = [
                {
                    name: "requests",
                    colorByPoint: true,
                    data: value?.formattedData.map((item) => ({
                        name: item[value?.xAxis],
                        y: item?.requests || 0,
                    })),
                },
            ];
        } else {
            seriesData = [{ name: "requests", data: value?.formattedData?.map(item => item?.requests) }];
        }
        return {
            chart: {
                type: value?.chartType?.chart_type_key || "line",
            },
            credits: {
                enabled: false
            },
            title: {
                text: value?.chartName,
            },
            xAxis: {
                categories: value?.formattedData?.map((item) => item[value?.xAxis]),
            },
            yAxis: {
                title: {
                    text: "requests",
                },
                min: 0,
            },
            series: seriesData,
            plotOptions: {
                pie: {
                    allowPointSelect: true,
                    cursor: 'pointer',
                    dataLabels: {
                        enabled: true,
                        format: '<b>{point.name}</b> ( {point.y} ) [{point.percentage:.1f} %]',
                    },
                },
                series: {
                    borderWidth: 0,
                    dataLabels: {
                        enabled: true,
                        formatter: function () {
                            var pcnt = (this.y / this?.series?.data.map(p => p.y).reduce((a, b) => a + b, 0)) * 100;
                            return `${this.y} [ ${parseInt(pcnt || 0) + '%'} ]`;
                        }
                    },
                }
            },
        };
    }

    const chartOpts = generateOptions(createChart);

    return { createChart, setCreateChart, loader, createOpts, options, chartOpts, saveData, savedCharts, handleDelete, deleteChart, setDeleteChart }
}